import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { getNavForUser, ROLE_LABELS } from "@/lib/permissions";
import NotificationBell from "@/components/NotificationBell";
import NotificationPermButton from "@/components/NotificationPermButton";
import useWebPush from "@/hooks/useWebPush";
import {
  ArrowSquareIn,
  ChartBar,
  House,
  Stack,
  Scissors,
  Package,
  UsersThree,
  SignOut,
  ClockCounterClockwise,
  UserGear,
  CloudArrowDown,
  Factory,
  ClipboardText,
  Clock,
  Receipt,
  Truck,
  BookmarkSimple,
  ArrowUUpLeft,
  WarningOctagon,
  Star,
  NotePencil,
  Prohibit,
  Storefront,
  Palette,
  Tag,
  Buildings,
  MapPin,
  ChartLineUp,
  CurrencyCircleDollar,
  FolderOpen,
  List as ListIcon,
  X as XIcon,
} from "@phosphor-icons/react";

const NAV_ITEMS = {
  dashboard:       { to: "/",                     label: "Panel",              icon: House },
  stock:           { to: "/stok",                 label: "Stok",               icon: Stack },
  cutting_tasks:   { to: "/kesim-gorevleri",      label: "Kesim Görevleri",    icon: ClipboardText },
  cutting_history: { to: "/kesim",                label: "Kesim Geçmişi",      icon: Scissors },
  incoming_history: { to: "/gelen",               label: "Gelen Geçmişi",      icon: ArrowSquareIn },
  statistics: { to: "/istatistik",               label: "Genel İstatistik",    icon: ChartBar },
  products:        { to: "/urunler",              label: "Ürünler",            icon: Package },
  customers:       { to: "/musteriler",           label: "Müşteriler",         icon: UsersThree },
  customer_addresses: { to: "/musteri-adresleri", label: "Müşteri Adresleri",  icon: MapPin },
  retail_orders:   { to: "/siparisler",           label: "Perakende Siparişler", icon: Receipt },
  pending_orders:  { to: "/bekleyen",             label: "Bekleyen Müşteri Siparişleri", icon: Clock },
  reservations:    { to: "/opsiyonlar",           label: "Opsiyonlar",         icon: BookmarkSimple },
  factory_orders:  { to: "/fabrika-siparisleri",  label: "Fabrika Siparişleri", icon: Factory },
  shipping:        { to: "/sevkiyat",             label: "Sevkiyat",           icon: Truck },
  cargo:           { to: "/kargo",                 label: "Kargo Teslimleri",   icon: Package },
  store_delivery:  { to: "/magaza-teslim",        label: "Mağaza Teslim",      icon: Storefront },
  proforma:        { to: "/proforma",              label: "Proforma",           icon: NotePencil },
  blacklist:       { to: "/kara-liste",             label: "Kara Liste",         icon: Prohibit },
  kartela:         { to: "/kartela",               label: "Kartela Stoğu",      icon: Palette },
  kartela_requests: { to: "/kartela-talepleri",   label: "Kartela Talepleri",  icon: ClipboardText },
  retail_prices:   { to: "/perakende-fiyat",       label: "Perakende Fiyatlar", icon: Tag },
  wholesale_prices:{ to: "/toptan-fiyat",          label: "Toptan Fiyatlar",    icon: Storefront },
  special_prices:  { to: "/ozel-fiyatlar",         label: "Özel Fiyatlar",      icon: CurrencyCircleDollar },
  purchase_prices: { to: "/gelis-fiyatlari",       label: "Geliş Fiyatları",    icon: CurrencyCircleDollar },
  account_statements: { to: "/muhasebe-ekstreleri", label: "Muhasebe Ekstreleri", icon: FolderOpen },
  factories:       { to: "/fabrikalar",            label: "Fabrikalar",         icon: Buildings },
  returns:         { to: "/iadeler",              label: "İadeler",            icon: ArrowUUpLeft },
  defect_report:   { to: "/defo-raporu",          label: "Defo Raporu",        icon: WarningOctagon },
  customer_stats:  { to: "/musteri-istatistik",   label: "Müşteri İstatistiği", icon: ChartLineUp },
  my_customers:    { to: "/musterilerim",         label: "Müşterilerim",       icon: Star },
  sales_notes:     { to: "/pazarlama-notlari",    label: "Pazarlama Notları",  icon: NotePencil },
  activity:        { to: "/gecmis",               label: "İşlem Geçmişi",      icon: ClockCounterClockwise },
  users:           { to: "/kullanicilar",         label: "Kullanıcılar",       icon: UserGear },
  backups:         { to: "/yedekler",             label: "Yedekler",           icon: CloudArrowDown },
};

export default function Layout() {
  const { user, logout } = useAuth();
  useWebPush();
  const navigate = useNavigate();
  const location = useLocation();
  const allowed = getNavForUser(user);
  const nav = allowed.map((k) => ({ key: k, ...NAV_ITEMS[k] })).filter((x) => x.to);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Route değişince mobil menüyü otomatik kapat
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-card">
        <div className="px-6 py-6 border-b border-border">
          <img
            src="/mossline-logo.png"
            alt="Mossline Home Textile"
            className="h-10 w-auto mx-auto"
          />
          <div className="text-[10px] text-center text-muted-foreground uppercase tracking-widest mt-2">
            Stok Programı
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground truncate">
                {user?.email}
              </div>
              <div className="text-sm font-medium truncate">
                {user?.name}
                <span className="text-[10px] text-primary uppercase tracking-widest ml-2">
                  {user?.title || ROLE_LABELS[user?.role] || user?.role}
                </span>
              </div>
            </div>
            <NotificationBell placement="top" />
            <NotificationPermButton />
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              navigate("/giris");
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted transition-colors duration-150"
          >
            <SignOut size={18} weight="duotone" />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Mobil üst bar: hamburger menü + logo + çıkış */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(true)}
            className="p-1 text-foreground hover:bg-muted rounded-md"
            aria-label="Menüyü aç"
          >
            <ListIcon size={24} weight="bold" />
          </button>
          <img src="/mossline-logo.png" alt="Mossline" className="h-7 w-auto" />
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
            {user?.title || ROLE_LABELS[user?.role] || user?.role}
          </div>
        </div>
        <button
          data-testid="logout-btn-mobile"
          onClick={() => {
            logout();
            navigate("/giris");
          }}
          className="text-sm text-muted-foreground ml-2"
        >
          Çıkış
        </button>
        <NotificationBell />
        <NotificationPermButton />
      </div>

      {/* Mobil menü paneli (üstten aşağı slide) */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col" data-testid="mobile-menu-panel">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          {/* Slide-down panel */}
          <div className="relative bg-card border-b border-border shadow-2xl max-h-[85vh] flex flex-col animate-in slide-in-from-top duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <img src="/mossline-logo.png" alt="Mossline" className="h-8 w-auto" />
                <div>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-[10px] text-primary uppercase tracking-widest">
                    {ROLE_LABELS[user?.role] || user?.role}
                  </div>
                </div>
              </div>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMobileOpen(false)}
                className="p-1 text-muted-foreground hover:text-foreground"
                aria-label="Menüyü kapat"
              >
                <XIcon size={22} weight="bold" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
              {nav.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.key}
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`nav-mobile-${n.key}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-md text-base transition-colors ${
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-foreground hover:bg-muted"
                      }`
                    }
                  >
                    <Icon size={22} weight="duotone" />
                    <span className="font-medium">{n.label}</span>
                  </NavLink>
                );
              })}
            </nav>
            <button
              onClick={() => {
                logout();
                navigate("/giris");
              }}
              className="flex items-center justify-center gap-2 px-4 py-3 border-t border-border text-sm text-muted-foreground hover:bg-muted"
              data-testid="mobile-menu-logout"
            >
              <SignOut size={18} weight="duotone" /> Çıkış Yap
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 md:pt-0 pt-14">
        <div className="max-w-[1400px] mx-auto p-4 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
